import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Plus, ArrowDownUp } from "lucide-react";

import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { EmptyState } from "@/components/shared/EmptyState";
import { ClientSummaryCatalogDialog } from "@/components/features/clients/ClientSummaryCatalogDialog";
import {
  ClientSummaryPeriodBlock,
  type SummaryUnmatchedRow,
} from "@/components/features/clients/ClientSummaryPeriodBlock";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { defaultCatalogTaxCategoryForClient } from "@/lib/client-invoice-defaults";
import { clientKeys, invoiceKeys } from "@/lib/queryKeys";
import { createClientCatalogItem } from "@/services/clientCatalogService";
import { getClient } from "@/services/clientService";
import { getCustomerInvoiceSummary } from "@/services/invoiceService";
import type { TaxCategory } from "@fiscalo/shared-types";
import { taxOptionsForRegime } from "@/lib/taxOptions";
import { useTenantSpecialRegime } from "@/lib/useTenantSpecialRegime";

function formatMoney(currency: string, amount: number, locale: string): string {
  return `${currency} ${amount.toLocaleString(locale === "es" ? "es-UY" : "en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** First line only when prepopulating catalog from an invoice line (multi-line descriptions). */
function firstLineForCatalogDescription(text: string): string {
  const first = text.split(/\r?\n/)[0];
  return first.trim();
}

export function ClientSummaryTab({
  customerId,
}: {
  customerId: string;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const year = new Date().getFullYear();
  const locale = i18n.language;

  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [catalogSku, setCatalogSku] = useState("");
  const [catalogDescription, setCatalogDescription] = useState("");
  const [catalogUnitPrice, setCatalogUnitPrice] = useState("");
  // The OPTION id, not the category: 2,2% and 2,75% are both vat_other, so a
  // category cannot say which the user picked.
  const [catalogTaxOptionId, setCatalogTaxOptionId] = useState<string>("vat_basic");
  const catalogSpecialRegime = useTenantSpecialRegime();
  const catalogTaxOptions = taxOptionsForRegime(catalogSpecialRegime);
  const catalogTaxOption =
    catalogTaxOptions.find((o) => o.id === catalogTaxOptionId) ??
    catalogTaxOptions[0];
  const [unmatchedSelectedYear, setUnmatchedSelectedYear] = useState<Set<string>>(new Set());
  const [unmatchedSelectedAll, setUnmatchedSelectedAll] = useState<Set<string>>(new Set());
  const [catalogDialogSourceLineCount, setCatalogDialogSourceLineCount] = useState(1);
  const [catalogDialogCombinedUyu, setCatalogDialogCombinedUyu] = useState(0);
  const [catalogDialogCombinedUsd, setCatalogDialogCombinedUsd] = useState(0);

  const resetCatalogForm = useCallback((): void => {
    setCatalogSku("");
    setCatalogDescription("");
    setCatalogUnitPrice("");
    setCatalogTaxOptionId("vat_basic");
    setCatalogDialogSourceLineCount(1);
    setCatalogDialogCombinedUyu(0);
    setCatalogDialogCombinedUsd(0);
  }, []);

  const { data: clientDetail } = useQuery({
    queryKey: clientKeys.detail(customerId),
    queryFn: () => getClient(customerId),
    staleTime: 60_000,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: invoiceKeys.customerSummary(customerId),
    queryFn: () => getCustomerInvoiceSummary(customerId),
    staleTime: 60_000,
  });

  const createCatalogMut = useMutation({
    mutationFn: () =>
      createClientCatalogItem(customerId, {
        sku: catalogSku.trim() || undefined,
        description: catalogDescription.trim(),
        unitPrice: Number(catalogUnitPrice),
        defaultTaxCategory: catalogTaxOption.category,
        defaultTaxRate: catalogTaxOption.rate,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: clientKeys.catalog(customerId) });
      await queryClient.invalidateQueries({
        queryKey: invoiceKeys.customerSummary(customerId),
      });
      toast.success(t("clients.catalog.toast.created"));
      setCatalogDialogOpen(false);
      resetCatalogForm();
    },
    onError: () => {
      toast.error(t("clients.catalog.toast.saveError"));
    },
  });

  const openAddCatalogFromUnmatched = useCallback(
    (row: SummaryUnmatchedRow) => {
      resetCatalogForm();
      setCatalogDialogSourceLineCount(1);
      setCatalogDialogCombinedUyu(row.amountUYU);
      setCatalogDialogCombinedUsd(row.amountUSD);
      setCatalogDescription(firstLineForCatalogDescription(row.description));
      // Safe as an option id: this helper only ever returns "export" or
      // "vat_basic", and every STANDARD option's id equals its category name.
      // Only the régimen options have ids that differ from their category.
      setCatalogTaxOptionId(defaultCatalogTaxCategoryForClient(clientDetail));
      setCatalogDialogOpen(true);
    },
    [resetCatalogForm, clientDetail]
  );

  const openAddCatalogFromSelectedUnmatched = useCallback(
    (rows: SummaryUnmatchedRow[], scope: "year" | "all") => {
      if (rows.length === 0) {
        return;
      }
      resetCatalogForm();
      setCatalogDialogSourceLineCount(rows.length);
      const uyu = rows.reduce((sum, r) => sum + r.amountUYU, 0);
      const usd = rows.reduce((sum, r) => sum + r.amountUSD, 0);
      setCatalogDialogCombinedUyu(uyu);
      setCatalogDialogCombinedUsd(usd);
      setCatalogDescription(firstLineForCatalogDescription(rows[0].description));
      // Safe as an option id: this helper only ever returns "export" or
      // "vat_basic", and every STANDARD option's id equals its category name.
      // Only the régimen options have ids that differ from their category.
      setCatalogTaxOptionId(defaultCatalogTaxCategoryForClient(clientDetail));
      setCatalogDialogOpen(true);
      if (scope === "year") {
        setUnmatchedSelectedYear(new Set());
      } else {
        setUnmatchedSelectedAll(new Set());
      }
    },
    [resetCatalogForm, clientDetail]
  );

  const handleCatalogDialogOpenChange = useCallback(
    (open: boolean) => {
      setCatalogDialogOpen(open);
      if (!open) resetCatalogForm();
    },
    [resetCatalogForm]
  );

  const handleSubmitCatalogItem = useCallback((): void => {
    const price = Number(catalogUnitPrice);
    if (!catalogDescription.trim()) return;
    if (!Number.isFinite(price) || price < 0) return;
    createCatalogMut.mutate();
  }, [catalogDescription, catalogUnitPrice, createCatalogMut]);

  useEffect(() => {
    if (isError) {
      toast.error(t("clients.detail.summary.loadError"));
    }
  }, [isError, t]);

  useEffect(() => {
    if (!data) {
      return;
    }
    const validYear = new Set(data.unmatchedLineSalesYear.map((u) => u.description));
    setUnmatchedSelectedYear((prev) => {
      const next = new Set<string>();
      for (const d of prev) {
        if (validYear.has(d)) {
          next.add(d);
        }
      }
      return next;
    });
    const validAll = new Set(data.unmatchedLineSalesAll.map((u) => u.description));
    setUnmatchedSelectedAll((prev) => {
      const next = new Set<string>();
      for (const d of prev) {
        if (validAll.has(d)) {
          next.add(d);
        }
      }
      return next;
    });
  }, [data]);

  const unmatchedLinesYear = data?.unmatchedLineSalesYear ?? [];
  const unmatchedLinesAll = data?.unmatchedLineSalesAll ?? [];

  const selectedUnmatchedYearRows = useMemo(
    () => unmatchedLinesYear.filter((r) => unmatchedSelectedYear.has(r.description)),
    [unmatchedLinesYear, unmatchedSelectedYear]
  );

  const selectedUnmatchedAllRows = useMemo(
    () => unmatchedLinesAll.filter((r) => unmatchedSelectedAll.has(r.description)),
    [unmatchedLinesAll, unmatchedSelectedAll]
  );

  const unmatchedYearHeaderCheckboxState = useMemo((): boolean | "indeterminate" => {
    if (unmatchedLinesYear.length === 0) {
      return false;
    }
    const n = unmatchedLinesYear.filter((r) => unmatchedSelectedYear.has(r.description)).length;
    if (n === 0) {
      return false;
    }
    if (n === unmatchedLinesYear.length) {
      return true;
    }
    return "indeterminate";
  }, [unmatchedLinesYear, unmatchedSelectedYear]);

  const unmatchedAllHeaderCheckboxState = useMemo((): boolean | "indeterminate" => {
    if (unmatchedLinesAll.length === 0) {
      return false;
    }
    const n = unmatchedLinesAll.filter((r) => unmatchedSelectedAll.has(r.description)).length;
    if (n === 0) {
      return false;
    }
    if (n === unmatchedLinesAll.length) {
      return true;
    }
    return "indeterminate";
  }, [unmatchedLinesAll, unmatchedSelectedAll]);

  const toggleUnmatchedYearRow = useCallback((description: string): void => {
    setUnmatchedSelectedYear((prev) => {
      const next = new Set(prev);
      if (next.has(description)) {
        next.delete(description);
      } else {
        next.add(description);
      }
      return next;
    });
  }, []);

  const toggleUnmatchedAllRow = useCallback((description: string): void => {
    setUnmatchedSelectedAll((prev) => {
      const next = new Set(prev);
      if (next.has(description)) {
        next.delete(description);
      } else {
        next.add(description);
      }
      return next;
    });
  }, []);

  const onUnmatchedYearHeaderCheckboxChange = useCallback(
    (checked: boolean | "indeterminate"): void => {
      if (checked === true) {
        setUnmatchedSelectedYear(new Set(unmatchedLinesYear.map((r) => r.description)));
      } else {
        setUnmatchedSelectedYear(new Set());
      }
    },
    [unmatchedLinesYear]
  );

  const onUnmatchedAllHeaderCheckboxChange = useCallback(
    (checked: boolean | "indeterminate"): void => {
      if (checked === true) {
        setUnmatchedSelectedAll(new Set(unmatchedLinesAll.map((r) => r.description)));
      } else {
        setUnmatchedSelectedAll(new Set());
      }
    },
    [unmatchedLinesAll]
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center py-12">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center py-12">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  const matchedYear = data.matchedCatalogSalesYear ?? [];
  const unmatchedYear = data.unmatchedLineSalesYear ?? [];
  const matchedAll = data.matchedCatalogSalesAll ?? [];
  const unmatchedAll = data.unmatchedLineSalesAll ?? [];
  const adjustmentsYear = data.adjustmentsYear ?? [];
  const adjustmentsAll = data.adjustmentsAll ?? [];
  const hasAnyLines =
    matchedYear.length + unmatchedYear.length + matchedAll.length + unmatchedAll.length > 0;
  const hasAdjustments = adjustmentsYear.length + adjustmentsAll.length > 0;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="transition-colors duration-150">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-foreground">
              {t("clients.detail.summary.invoiceCountYearLabel", { year })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums text-foreground">
              {data.invoiceCountYear}
            </p>
          </CardContent>
        </Card>
        <Card className="transition-colors duration-150">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-foreground">
              {t("clients.detail.summary.invoiceCountAllLabel")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums text-foreground">
              {data.invoiceCountAll}
            </p>
          </CardContent>
        </Card>
        <Card className="transition-colors duration-150">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-foreground">
              {t("clients.detail.summary.salesYearTitle", { year })}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p className="tabular-nums text-foreground">
              {formatMoney("UYU", data.salesAcceptedYear.UYU, locale)}
            </p>
            <p className="tabular-nums text-foreground">
              {formatMoney("USD", data.salesAcceptedYear.USD, locale)}
            </p>
          </CardContent>
        </Card>
        <Card className="transition-colors duration-150">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-foreground">
              {t("clients.detail.summary.salesAllTimeTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p className="tabular-nums text-foreground">
              {formatMoney("UYU", data.salesAcceptedAll.UYU, locale)}
            </p>
            <p className="tabular-nums text-foreground">
              {formatMoney("USD", data.salesAcceptedAll.USD, locale)}
            </p>
          </CardContent>
        </Card>
      </div>

      <p className="text-sm text-muted-foreground">{t("clients.detail.summary.salesHint")}</p>

      {hasAdjustments ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ArrowDownUp className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base text-foreground">
                {t("clients.detail.summary.adjustmentsTitle", "Ajustes")}
              </CardTitle>
            </div>
            <CardDescription>
              {t("clients.detail.summary.adjustmentsDescription", "Líneas de notas de crédito y débito confirmadas por DGI.")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {adjustmentsYear.length > 0 ? (
                <div>
                  <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                    {t("clients.detail.summary.productBreakdownSectionYear", { year })}
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("clients.detail.summary.adjustmentsCfeType", "Tipo")}</TableHead>
                        <TableHead>{t("clients.detail.summary.adjustmentsLineDescription", "Descripción")}</TableHead>
                        <TableHead className="text-right">{t("clients.detail.summary.columnUyu", "UYU")}</TableHead>
                        <TableHead className="text-right">{t("clients.detail.summary.columnUsd", "USD")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adjustmentsYear.map((adj, idx) => (
                        <TableRow key={idx} className="even:bg-muted/30">
                          <TableCell className="text-sm">
                            {t(`invoices.cfeTypes.${adj.cfeType}`, adj.cfeType)}
                          </TableCell>
                          <TableCell className="text-sm">{adj.description}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {adj.amountUYU !== 0 ? formatMoney("UYU", adj.amountUYU, locale) : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {adj.amountUSD !== 0 ? formatMoney("USD", adj.amountUSD, locale) : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : null}
              {adjustmentsAll.length > 0 ? (
                <div>
                  <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                    {t("clients.detail.summary.productBreakdownSectionAll")}
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("clients.detail.summary.adjustmentsCfeType", "Tipo")}</TableHead>
                        <TableHead>{t("clients.detail.summary.adjustmentsLineDescription", "Descripción")}</TableHead>
                        <TableHead className="text-right">{t("clients.detail.summary.columnUyu", "UYU")}</TableHead>
                        <TableHead className="text-right">{t("clients.detail.summary.columnUsd", "USD")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adjustmentsAll.map((adj, idx) => (
                        <TableRow key={idx} className="even:bg-muted/30">
                          <TableCell className="text-sm">
                            {t(`invoices.cfeTypes.${adj.cfeType}`, adj.cfeType)}
                          </TableCell>
                          <TableCell className="text-sm">{adj.description}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {adj.amountUYU !== 0 ? formatMoney("UYU", adj.amountUYU, locale) : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {adj.amountUSD !== 0 ? formatMoney("USD", adj.amountUSD, locale) : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {!hasAnyLines ? (
        <Card>
          <CardContent className="pt-6">
            <EmptyState
              title={t("clients.detail.summary.emptyProducts")}
              message={t("clients.detail.summary.emptyProductsHint")}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">
              {t("clients.detail.summary.productBreakdownSectionYear", { year })}
            </h3>
            <ClientSummaryPeriodBlock
              matchedTitle={t("clients.detail.summary.matchedCatalogTitleYear", { year })}
              matchedEmptyText={t("clients.detail.summary.matchedCatalogEmptyYear", { year })}
              unmatchedTitle={t("clients.detail.summary.unmatchedLinesTitleYear", { year })}
              matched={matchedYear}
              unmatched={unmatchedYear}
              locale={locale}
              formatMoney={formatMoney}
              t={t}
              unmatchedSelected={unmatchedSelectedYear}
              onToggleUnmatchedRow={toggleUnmatchedYearRow}
              onUnmatchedHeaderCheckboxChange={onUnmatchedYearHeaderCheckboxChange}
              unmatchedHeaderCheckboxState={unmatchedYearHeaderCheckboxState}
              selectedUnmatchedRows={selectedUnmatchedYearRows}
              onAddCatalogFromUnmatched={openAddCatalogFromUnmatched}
              onAddSelectedUnmatchedToCatalog={() =>
                openAddCatalogFromSelectedUnmatched(selectedUnmatchedYearRows, "year")
              }
            />
          </div>
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">
              {t("clients.detail.summary.productBreakdownSectionAll")}
            </h3>
            <ClientSummaryPeriodBlock
              matchedTitle={t("clients.detail.summary.matchedCatalogTitleAll")}
              matchedEmptyText={t("clients.detail.summary.matchedCatalogEmptyAll")}
              unmatchedTitle={t("clients.detail.summary.unmatchedLinesTitleAll")}
              matched={matchedAll}
              unmatched={unmatchedAll}
              locale={locale}
              formatMoney={formatMoney}
              t={t}
              unmatchedSelected={unmatchedSelectedAll}
              onToggleUnmatchedRow={toggleUnmatchedAllRow}
              onUnmatchedHeaderCheckboxChange={onUnmatchedAllHeaderCheckboxChange}
              unmatchedHeaderCheckboxState={unmatchedAllHeaderCheckboxState}
              selectedUnmatchedRows={selectedUnmatchedAllRows}
              onAddCatalogFromUnmatched={openAddCatalogFromUnmatched}
              onAddSelectedUnmatchedToCatalog={() =>
                openAddCatalogFromSelectedUnmatched(selectedUnmatchedAllRows, "all")
              }
            />
          </div>
        </div>
      )}

      <ClientSummaryCatalogDialog
        open={catalogDialogOpen}
        onOpenChange={handleCatalogDialogOpenChange}
        onSubmit={handleSubmitCatalogItem}
        isSubmitting={createCatalogMut.isPending}
        catalogSku={catalogSku}
        setCatalogSku={setCatalogSku}
        catalogDescription={catalogDescription}
        setCatalogDescription={setCatalogDescription}
        catalogUnitPrice={catalogUnitPrice}
        setCatalogUnitPrice={setCatalogUnitPrice}
        taxOptions={catalogTaxOptions}
        catalogTaxOptionId={catalogTaxOptionId}
        setCatalogTaxOptionId={setCatalogTaxOptionId}
        catalogDialogSourceLineCount={catalogDialogSourceLineCount}
        catalogDialogCombinedUyu={catalogDialogCombinedUyu}
        catalogDialogCombinedUsd={catalogDialogCombinedUsd}
        locale={locale}
        formatMoney={formatMoney}
        t={t}
      />
    </div>
  );
}
